import React, { useEffect, useState, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { approvalService } from 'api/services/approvalService';
import { bookingService } from 'api/services/bookingService';
import { useApproveAction, useRejectAction } from 'hooks/api/useApprovals';
import type { Approval } from 'api/types/approval.types';
import { isPostArrivalItem } from 'api/types/approval.types';
import { BOOKING_SOURCE } from 'components/modals/modules/booking-module/booking.utils';
import type { BookingFull } from 'api/types/booking.types';
import { formatDate, formatCurrency } from 'components/modals/modules/booking-module/booking.utils';
import { buildBookingTaxBreakdown } from 'utils/bookingTax';

// ============================================================================
// HELPERS
// ============================================================================

const TYPE_LABELS: Record<string, string> = {
  DISCOUNT: 'Discount',
  RATE_BELOW_MINIMUM: 'Rate Below Minimum',
  COMPLIMENTARY: 'Complimentary',
  REFUND: 'Refund',
  ADVANCE_OVERRIDE: 'Advance Override',
  OVERDUE_ADVANCE: 'Overdue Advance',
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  APPROVAL_PENDING: { bg: '#fef3c7', fg: '#92400e', label: 'Pending' },
  APPROVED:         { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
  REJECTED:         { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  ESCALATED:        { bg: '#dbeafe', fg: '#1e40af', label: 'Escalated' },
};

const ReadOnlyField: React.FC<{ label: string; value?: React.ReactNode; full?: boolean }> = ({ label, value, full }) => (
  <div style={full ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem',
      background: 'var(--bg-secondary)',
      borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)',
      fontSize: '0.85rem',
      minHeight: '2rem',
    }}>
      {value != null && value !== '' ? value : '-'}
    </div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode; first?: boolean }> = ({ children, first }) => (
  <h3 style={{
    fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)',
    margin: first ? '0 0 0.75rem 0' : '1.25rem 0 0.75rem 0',
    paddingTop: first ? 0 : '0.75rem',
    borderTop: first ? 'none' : '1px solid var(--border-color)',
  }}>{children}</h3>
);

/** Derive source-specific display for the Booking tab header. */
function sourceSummary(b: BookingFull['booking']): string {
  if (b.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT && b.agentName)
    return `Travel Agent — ${b.agentCode ? b.agentCode + ' - ' : ''}${b.agentName}`;
  if (b.bookingSource === BOOKING_SOURCE.CORPORATE && b.corporateName)
    return `Corporate — ${b.corporateCode ? b.corporateCode + ' - ' : ''}${b.corporateName}`;
  if (b.bookingSource === BOOKING_SOURCE.OTA) {
    const otaLabel = (b as any).otaDisplayName || b.otaName;
    if (otaLabel) return `OTA — ${(b as any).otaCode ? (b as any).otaCode + ' - ' : ''}${otaLabel}${b.otaBookingRef ? ' (Ref: ' + b.otaBookingRef + ')' : ''}`;
  }
  return b.bookingSource || '-';
}

// ============================================================================
// COMPONENT
// ============================================================================

export interface ApprovalActionModalProps {
  isOpen: boolean;
  approval: Approval | null;
  onClose: () => void;
  onActed: () => void;       // called after approve/reject succeed (parent refreshes inbox)
  /** Post-arrival items: open the Rate Worksheet for this item (re-price, then Save & Resolve). */
  onReprice?: (approval: Approval) => void;
}

type TabKey = 'request' | 'booking';

const ApprovalActionModal: React.FC<ApprovalActionModalProps> = ({
  isOpen, approval, onClose, onActed, onReprice,
}) => {
  const [current, setCurrent] = useState<Approval | null>(approval);
  const [fullBooking, setFullBooking] = useState<BookingFull | null>(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('request');
  const [notes, setNotes] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  const { act: approve, loading: approving } = useApproveAction();
  const { act: reject, loading: rejecting } = useRejectAction();

  // Refresh approval + fetch the linked booking on open.
  useEffect(() => {
    if (!isOpen || !approval) {
      setCurrent(null);
      setFullBooking(null);
      return;
    }
    setCurrent(approval);
    setNotes('');
    setActiveTab('request');
    setFullBooking(null);

    let cancelled = false;
    setLoadingDetail(true);
    approvalService.getById(approval.approvalRequestId)
      .then(res => { if (!cancelled && res?.data) setCurrent({ ...res.data, id: res.data.approvalRequestId }); })
      .catch(() => {/* keep stale prop on failure */})
      .finally(() => { if (!cancelled) setLoadingDetail(false); });

    if (approval.bookingId) {
      setLoadingBooking(true);
      bookingService.getFullBooking(approval.bookingId)
        .then(res => { if (!cancelled && res?.data) setFullBooking(res.data); })
        .catch(() => {/* leave booking tab with a friendly empty state */})
        .finally(() => { if (!cancelled) setLoadingBooking(false); });
    }

    return () => { cancelled = true; };
  }, [isOpen, approval]);

  const handleConfirmApprove = useCallback(async () => {
    if (!current) return;
    setApproveConfirmOpen(false);
    try {
      const postArrival = isPostArrivalItem(current.approvalType);
      await approve(current.approvalRequestId, {
        notes: notes.trim() || undefined,
        rowVersion: current.rowVersion,
        resolution: postArrival ? 'NO_CHANGE' : undefined,
      });
      notificationService.toast.success(postArrival
        ? 'Closed without a price change — the desk sees your note.'
        : 'Approval approved. Booking confirmed.');
      onActed();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to approve.';
      notificationService.toast.error(msg);
    }
  }, [current, notes, approve, onActed, onClose]);

  const handleConfirmReject = useCallback(async () => {
    if (!current) return;
    setRejectConfirmOpen(false);
    try {
      await reject(current.approvalRequestId, { notes: notes.trim() || undefined, rowVersion: current.rowVersion });
      notificationService.toast.success(isPostArrivalItem(current.approvalType)
        ? 'Request declined — the booking is unchanged and the desk sees your reason.'
        : 'Approval rejected. Booking cancelled and inventory released.');
      onActed();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to reject.';
      notificationService.toast.error(msg);
    }
  }, [current, notes, reject, onActed, onClose]);

  if (!isOpen || !current) return null;

  const isPending = current.approvalStatusCode === 'APPROVAL_PENDING';
  const postArrival = isPostArrivalItem(current.approvalType);
  const statusBadge = current.approvalStatusCode ? STATUS_BADGE[current.approvalStatusCode] : null;
  const isBusy = approving || rejecting;

  const booking = fullBooking?.booking;
  const detailLines = fullBooking?.detailLines || [];
  const guests = fullBooking?.guests || [];
  // GST per component and rate from the booking's tax snapshot; empty when tax is not calculated.
  const taxBreakdown = buildBookingTaxBreakdown(fullBooking?.taxLines);
  const primaryGuest = guests.find(g => g.isPrimary);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxWidth: '840px' }}>
        {isBusy && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {approving ? 'Approving...' : 'Rejecting...'}
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Approval Request</h2>
          <button className="modal-close" onClick={onClose} disabled={isBusy}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Status + Tab strip */}
        <div style={{
          padding: '0.625rem 1.25rem',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-secondary, #f8fafc)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {statusBadge && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 600, padding: '0.25rem 0.625rem', borderRadius: '0.25rem',
                background: statusBadge.bg, color: statusBadge.fg,
              }}>{statusBadge.label}</span>
            )}
            <span style={{
              fontSize: '0.75rem', fontWeight: 600, padding: '0.25rem 0.625rem', borderRadius: '0.25rem',
              background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)',
            }}>
              {TYPE_LABELS[current.approvalType] || current.approvalType}
            </span>
            {current.bookingNumber && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Booking: <strong style={{ color: 'var(--text-primary)' }}>{current.bookingNumber}</strong>
              </span>
            )}
            {loadingDetail && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Refreshing…</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }} role="tablist">
            <TabButton active={activeTab === 'request'} onClick={() => setActiveTab('request')}>
              Request
            </TabButton>
            <TabButton active={activeTab === 'booking'} onClick={() => setActiveTab('booking')}>
              Booking &amp; Charges{loadingBooking && ' (loading…)'}
            </TabButton>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {/* ========== REQUEST TAB ========== */}
          {activeTab === 'request' && (
            <>
              <div className="form-grid-3">
                <ReadOnlyField label="Property" value={
                  current.propertyCode && current.propertyName
                    ? `${current.propertyCode} — ${current.propertyName}`
                    : (current.propertyName || '-')
                } />
                <ReadOnlyField label="Booking Status" value={current.bookingStatus || current.bookingStatusCode || '-'} />
                <ReadOnlyField label="Type" value={TYPE_LABELS[current.approvalType] || current.approvalType} />
              </div>

              {/* Type-aware variance row. Discount/Comp shows discount amount %; Rate-Below-Min shows requested vs minimum;
                  Advance-Override shows the proposed new due date pulled from meta_data; Overdue-Advance shows the shortfall. */}
              {(() => {
                const t = current.approvalType;
                let proposedDate: string | null = null;
                if (current.metaData) {
                  try { proposedDate = JSON.parse(current.metaData)?.proposed_advance_due_date || null; }
                  catch { /* ignore */ }
                }
                if (t === 'ADVANCE_OVERRIDE' || t === 'OVERDUE_ADVANCE') {
                  return (
                    <div className="form-grid-3" style={{ marginTop: '1rem' }}>
                      <ReadOnlyField label="Booking Total" value={current.totalValue != null ? `₹${formatCurrency(current.totalValue)}` : '-'} />
                      <ReadOnlyField label="Variance Amount" value={current.varianceAmount != null ? `${current.varianceAmount < 0 ? '-' : ''}₹${formatCurrency(Math.abs(current.varianceAmount))}` : '-'} />
                      <ReadOnlyField label={t === 'ADVANCE_OVERRIDE' ? 'Proposed Due Date' : 'Days Overdue'} value={proposedDate || '-'} />
                    </div>
                  );
                }
                if (t === 'RATE_BELOW_MINIMUM') {
                  return (
                    <div className="form-grid-3" style={{ marginTop: '1rem' }}>
                      <ReadOnlyField label="Booking Total" value={current.totalValue != null ? `₹${formatCurrency(current.totalValue)}` : '-'} />
                      <ReadOnlyField label="Requested Rate" value={current.requestedRate != null ? `₹${formatCurrency(current.requestedRate)}` : '-'} />
                      <ReadOnlyField label="Minimum Rate" value={current.minimumRate != null ? `₹${formatCurrency(current.minimumRate)}` : '-'} />
                    </div>
                  );
                }
                // DISCOUNT / COMPLIMENTARY / REFUND default — keep the original labels.
                return (
                  <div className="form-grid-3" style={{ marginTop: '1rem' }}>
                    <ReadOnlyField label="Subtotal" value={current.totalValue != null ? `₹${formatCurrency(current.totalValue)}` : '-'} />
                    <ReadOnlyField label="Discount" value={current.varianceAmount != null ? `-₹${formatCurrency(current.varianceAmount)}` : '-'} />
                    <ReadOnlyField label="Discount %" value={current.variancePercent != null ? `${current.variancePercent.toFixed(2)}%` : '-'} />
                  </div>
                );
              })()}

              <div className="form-grid-2" style={{ marginTop: '1rem' }}>
                <ReadOnlyField label="Requested By" value={current.requestedByName || '-'} />
                <ReadOnlyField label="Requested At" value={formatDate(current.requestedAt)} />
              </div>

              <div style={{ marginTop: '1rem' }}>
                <ReadOnlyField label="Justification" value={
                  <span style={{ whiteSpace: 'pre-wrap' }}>{current.justification || '-'}</span>
                } full />
              </div>

              {!isPending && (current.approvedByName || current.approvedAt) && (
                <>
                  <hr style={{ margin: '1rem 0', borderColor: 'var(--border-color)' }} />
                  <div className="form-grid-2">
                    <ReadOnlyField label="Decided By" value={current.approvedByName || '-'} />
                    <ReadOnlyField label="Decided At" value={current.approvedAt ? formatDate(current.approvedAt) : '-'} />
                  </div>
                  {current.approvalNotes && (
                    <div style={{ marginTop: '1rem' }}>
                      <ReadOnlyField label="Decision Notes" value={
                        <span style={{ whiteSpace: 'pre-wrap' }}>{current.approvalNotes}</span>
                      } full />
                    </div>
                  )}
                </>
              )}

              {isPending && (
                <>
                  <hr style={{ margin: '1rem 0', borderColor: 'var(--border-color)' }} />
                  <div>
                    <label className="form-label">Notes (optional)</label>
                    <textarea
                      className="form-textarea"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      placeholder="Optional notes for the audit trail"
                      style={{ resize: 'vertical', minHeight: '60px' }}
                      disabled={isBusy}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {/* ========== BOOKING & CHARGES TAB ========== */}
          {activeTab === 'booking' && (
            <>
              {loadingBooking && !booking && (
                <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Loading booking details…
                </div>
              )}

              {!loadingBooking && !booking && (
                <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Booking details could not be loaded.
                </div>
              )}

              {booking && (
                <>
                  <SectionTitle first>Source &amp; Guest</SectionTitle>
                  <div className="form-grid-3">
                    <ReadOnlyField label="Source" value={sourceSummary(booking)} />
                    <ReadOnlyField label="Guest Name" value={booking.guestFullName || primaryGuest?.firstName || '-'} />
                    <ReadOnlyField label="Guest Phone" value={booking.guestPhone || '-'} />
                  </div>
                  {(booking.agentName || booking.corporateName || booking.otaName || (booking as any).otaDisplayName) && (
                    <div className="form-grid-3" style={{ marginTop: '1rem' }}>
                      {booking.agentName && <ReadOnlyField label="Agent" value={`${booking.agentCode ?? ''} ${booking.agentCode ? '—' : ''} ${booking.agentName}`.trim()} />}
                      {booking.corporateName && <ReadOnlyField label="Corporate" value={`${booking.corporateCode ?? ''} ${booking.corporateCode ? '—' : ''} ${booking.corporateName}`.trim()} />}
                      {(() => {
                        const otaLabel = (booking as any).otaDisplayName || booking.otaName;
                        if (!otaLabel) return null;
                        const code = (booking as any).otaCode;
                        const display = code ? `${code} — ${otaLabel}` : otaLabel;
                        return <ReadOnlyField label="OTA" value={booking.otaBookingRef ? `${display} (Ref: ${booking.otaBookingRef})` : display} />;
                      })()}
                    </div>
                  )}

                  <SectionTitle>Stay</SectionTitle>
                  <div className="form-grid-4">
                    <ReadOnlyField label="Check-In" value={formatDate(booking.checkInDate)} />
                    <ReadOnlyField label="Check-Out" value={formatDate(booking.checkOutDate)} />
                    <ReadOnlyField label="Nights" value={booking.numNights} />
                    <ReadOnlyField label="Total Rooms" value={booking.totalRooms} />
                  </div>
                  <div className="form-grid-3" style={{ marginTop: '1rem' }}>
                    <ReadOnlyField label="Adults" value={booking.numAdults} />
                    <ReadOnlyField label="Children" value={booking.numChildren ?? 0} />
                    <ReadOnlyField label="VIP / Comp" value={
                      booking.isComplimentary ? 'Complimentary' : (booking.isVip ? 'VIP' : 'Standard')
                    } />
                  </div>
                  {booking.isComplimentary && booking.complimentaryReason && (
                    <div style={{ marginTop: '1rem' }}>
                      <ReadOnlyField label="Complimentary Reason" value={booking.complimentaryReason} full />
                    </div>
                  )}

                  <SectionTitle>Line Items</SectionTitle>
                  {detailLines.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      No detail lines found for this booking.
                    </div>
                  ) : (
                    <div style={{
                      border: '1px solid var(--border-color)', borderRadius: '0.375rem',
                      overflow: 'hidden', fontSize: '0.78rem',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>Room</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>Rate / Meal</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>Rooms</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>Pax (A/C)</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Room</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Meal</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Extras</th>
                            {/* Approvers must see WHAT was negotiated, not just the
                                standard figures — this is the internal split. */}
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Standard</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Adjustment</th>
                            <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'right' }}>Selling</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailLines.map((l, i) => (
                            <tr key={l.bookingDetailId ?? i} style={{ borderBottom: i === detailLines.length - 1 ? 'none' : '1px solid var(--border-color)' }}>
                              <td style={{ padding: '0.5rem' }}>
                                <div style={{ fontWeight: 500 }}>{l.categoryCode || '-'}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>{l.categoryName}</div>
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <div>{l.rateName || '-'}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                                  {l.mealPlanCode || l.mealPlanName || '-'}
                                </div>
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'center' }}>{l.numberOfRooms}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'center' }}>{l.numAdults}/{l.numChildren ?? 0}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(l.totalRoomCharges ?? 0)}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(l.totalMealCharges ?? 0)}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(l.totalExtraCharges ?? 0)}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(l.lineTotal ?? 0)}</td>
                              <td style={{
                                padding: '0.5rem', textAlign: 'right', fontWeight: 600,
                                color: (l.adjustmentAmount ?? 0) === 0 ? 'var(--text-muted)'
                                  : (l.adjustmentAmount ?? 0) < 0 ? '#b91c1c' : '#b45309',
                              }}>
                                {(l.adjustmentAmount ?? 0) === 0 ? '—' : (
                                  <>
                                    {(l.adjustmentAmount ?? 0) > 0 ? '+' : '−'}
                                    {formatCurrency(Math.abs(l.adjustmentAmount ?? 0))}
                                  </>
                                )}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                                {formatCurrency(l.sellingLineTotal ?? l.lineTotal ?? 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <SectionTitle>Charges Breakdown</SectionTitle>
                  <div style={{
                    border: '1px solid var(--border-color)', borderRadius: '0.375rem',
                    padding: '0.75rem 1rem', fontSize: '0.85rem',
                    display: 'flex', flexDirection: 'column', gap: '0.375rem',
                    maxWidth: '480px',
                  }}>
                    <BreakdownRow label="Room Charges" value={formatCurrency(booking.roomCharges ?? 0)} />
                    <BreakdownRow label="Meal Charges" value={formatCurrency(booking.mealCharges ?? 0)} />
                    <BreakdownRow label="Extra Charges" value={formatCurrency(booking.extraCharges ?? 0)} />
                    <div style={{ borderTop: '1px solid var(--border-color)', margin: '0.25rem 0' }} />
                    <BreakdownRow label="Subtotal" value={formatCurrency(booking.subtotal ?? 0)} bold />
                    {(booking.taxAmount ?? 0) > 0 && !taxBreakdown.hasLines && (
                      <BreakdownRow label="Tax" value={formatCurrency(booking.taxAmount ?? 0)} />
                    )}
                    {((booking.discountAmount ?? 0) > 0 || booking.isComplimentary) && (
                      <BreakdownRow
                        label={
                          booking.isComplimentary
                            ? 'Complimentary booking'
                            : booking.discountType === 'Percentage'
                              ? `Discount (${booking.discountValue ?? 0}%)`
                              : booking.discountType === 'FlatAmount'
                                ? `Discount (Flat ₹${(booking.discountValue ?? 0).toLocaleString('en-IN')})`
                                : 'Discount'
                        }
                        value={`-${formatCurrency(booking.discountAmount ?? 0)}`}
                        accent="#b45309"
                      />
                    )}
                    {booking.discountRemarks && !booking.isComplimentary && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', paddingLeft: '0.25rem' }}>
                        {booking.discountRemarks}
                      </div>
                    )}
                    {(taxBreakdown.charges.length > 0 || taxBreakdown.chargesIncluded.length > 0) && (
                      <>
                        <div style={{ borderTop: '1px solid var(--border-color)', margin: '0.25rem 0' }} />
                        <BreakdownRow
                          label={taxBreakdown.chargesIncluded.length > 0 ? 'Charges after discount' : 'Taxable Value'}
                          value={formatCurrency((booking.netAmount ?? booking.grandTotal ?? 0) - taxBreakdown.chargesTaxTotal)}
                        />
                        {taxBreakdown.charges.map((r) => (
                          <BreakdownRow key={r.key} label={r.label} value={formatCurrency(r.amount)} />
                        ))}
                        {taxBreakdown.chargesIncluded.map((r) => (
                          <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-secondary)', paddingLeft: '0.75rem' }}>
                            <span>Includes {r.label}</span>
                            <span>{formatCurrency(r.amount)}</span>
                          </div>
                        ))}
                      </>
                    )}
                    <div style={{ borderTop: '2px solid var(--border-color)', margin: '0.25rem 0' }} />
                    <BreakdownRow
                      label={((booking.discountAmount ?? 0) > 0 || booking.isComplimentary) ? 'Net Total' : 'Grand Total'}
                      value={formatCurrency(booking.netAmount ?? booking.grandTotal ?? 0)}
                      bold
                      accent="#2563eb"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {isPending && postArrival ? (
            <>
              {/* Post-arrival items are never "approved": re-price, close without a
                  price change, or decline — the last two with a reason for the desk. */}
              <button className="btn btn-secondary" onClick={onClose} disabled={isBusy}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={() => setRejectConfirmOpen(true)}
                disabled={isBusy || notes.trim().length < 3}
                title={notes.trim().length < 3 ? 'Write the reason in the notes first' : undefined}
              >
                <IconSystem.x size={16} />
                Decline
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setApproveConfirmOpen(true)}
                disabled={isBusy || notes.trim().length < 3}
                title={notes.trim().length < 3 ? 'Write why there is no price change in the notes first' : undefined}
              >
                Close — no price change
              </button>
              {onReprice && current.bookingId && (
                <button
                  className="btn btn-primary"
                  onClick={() => { onReprice(current); onClose(); }}
                  disabled={isBusy}
                >
                  <IconSystem.edit size={16} />
                  Re-price (Rate Worksheet)
                </button>
              )}
            </>
          ) : isPending ? (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={isBusy}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={() => setRejectConfirmOpen(true)}
                disabled={isBusy}
              >
                <IconSystem.x size={16} />
                Reject
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setApproveConfirmOpen(true)}
                disabled={isBusy}
              >
                <IconSystem.check size={16} />
                Approve
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={approveConfirmOpen}
        title={postArrival ? 'Close without a price change?' : 'Approve booking?'}
        message={postArrival
          ? `The request on booking ${current.bookingNumber || ''} is closed and the booking price is NOT changed. Your note goes to the front office.`
          : `This will mark approval ${current.approvalRequestId.slice(0, 8)} as APPROVED and move booking ${current.bookingNumber || ''} to CONFIRMED.`}
        confirmLabel={postArrival ? 'Close' : 'Approve'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmApprove}
        onCancel={() => setApproveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={rejectConfirmOpen}
        title={postArrival ? 'Decline the request?' : 'Reject and cancel booking?'}
        message={postArrival
          ? `The request on booking ${current.bookingNumber || ''} is declined. The booking itself is not changed. Your reason goes to the front office.`
          : `This rejects the approval AND cancels booking ${current.bookingNumber || ''}. The held inventory for the stay span will be released. This cannot be undone.`}
        confirmLabel={postArrival ? 'Decline' : 'Reject & Cancel'}
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmReject}
        onCancel={() => setRejectConfirmOpen(false)}
      />
    </>
  );
};

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    style={{
      padding: '0.375rem 0.875rem',
      fontSize: '0.8rem',
      fontWeight: active ? 600 : 500,
      color: active ? '#2563eb' : 'var(--text-secondary)',
      background: active ? 'var(--bg-primary)' : 'transparent',
      border: '1px solid',
      borderColor: active ? '#2563eb' : 'var(--border-color)',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    }}
  >
    {children}
  </button>
);

const BreakdownRow: React.FC<{ label: string; value: string; bold?: boolean; accent?: string }> = ({ label, value, bold, accent }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between',
    fontWeight: bold ? 600 : 400,
    color: accent || 'var(--text-primary)',
  }}>
    <span>{label}</span>
    <span>₹{value}</span>
  </div>
);

export default ApprovalActionModal;
